import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const email = location.state?.email || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email not found. Please register again.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.verifyEmail(email, otp);
      login(response.data.user, response.data.token);
      toast.success('Email verified successfully!');
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Verification failed. Please check the code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-extrabold text-indigo-600">RaffleBowl</Link>
        </div>

        <Card padding="lg">
          <div className="text-center mb-6">
            <div className="bg-indigo-100 dark:bg-indigo-900 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-7 w-7 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Verify Your Email</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              We've sent a verification code to{' '}
              {email ? <span className="font-medium text-gray-700 dark:text-gray-300">{email}</span> : 'your email'}.
              Enter it below.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mb-5">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input label="Verification Code" type="text" value={otp} onChange={(e) => { setOtp(e.target.value); setError(''); }} placeholder="Enter 6-digit code" required />
            <Button type="submit" fullWidth isLoading={isLoading} className="py-3 text-base">Verify Email</Button>
          </form>
        </Card>
      </div>
    </div>
  );
};
